import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import heroAtto1 from '../images/kv-pc.jpg';
import exteriorAtto1 from '../images/07-01-pc-01.webp';
import interiorAtto1 from '../images/10-pc.jpg';
import colorAtto1 from '../images/04-black.webp';
import PageSeo, {
  SITE,
  breadcrumbJsonLd,
  keywordSets,
  localBusinessJsonLd,
} from '../PageSeo';

function Atto1Junius() {
  const waLink =
    'https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20ingin%20konsultasi%20BYD%20Atto%201%20dengan%20Junius%20di%20BYD%20BSD%20Serpong';

  const supportCards = [
    {
      image: exteriorAtto1,
      title: 'Konsultasi unit dan ketersediaan warna',
      description:
        'Dipakai untuk user yang sudah tahu modelnya dan hanya butuh kontak langsung untuk lanjut bertanya soal warna, varian, atau unit display.',
    },
    {
      image: interiorAtto1,
      title: 'Booking test drive dan follow up pembelian',
      description:
        'Halaman ini sengaja dibuat sebagai support page agar pengguna bisa cepat pindah dari browsing ke komunikasi langsung dengan Junius.',
    },
    {
      image: colorAtto1,
      title: 'Jembatan ke halaman utama dan halaman harga',
      description:
        'Semua intent utama tetap diarahkan ke halaman produk dan halaman harga agar struktur keyword lebih rapi dan tidak saling cannibalize.',
    },
  ];

  const personJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Junius',
    jobTitle: 'Marketing Consultant',
    worksFor: {
      '@id': `${SITE.url}/#dealer`,
    },
    telephone: SITE.phone,
    url: `${SITE.url}/blog/byd-atto-1-bsd-serpong-junius`,
  };

  return (
    <>
      <PageSeo
        title='Junius BYD BSD Serpong | Konsultasi BYD Atto 1, Harga, Test Drive'
        description='Halaman support untuk konsultasi BYD Atto 1 bersama Junius di BYD BSD Serpong. Cocok untuk kontak langsung, follow up unit, dan booking test drive.'
        keywords={[
          ...keywordSets.localDealer,
          'Junius BYD BSD Serpong',
          'Atto 1 Junius',
          'kontak sales BYD BSD Serpong',
          'konsultasi BYD Atto 1',
        ]}
        path='/blog/byd-atto-1-bsd-serpong-junius'
        image={heroAtto1}
        type='profile'
        robots='noindex, follow'
        jsonLd={[
          localBusinessJsonLd(),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            {
              name: 'Konsultasi BYD Atto 1 dengan Junius',
              path: '/blog/byd-atto-1-bsd-serpong-junius',
            },
          ]),
          personJsonLd,
        ]}
      />

      <Box component='main' sx={{ width: '100%', overflow: 'hidden' }}>
        <HeroSection waLink={waLink} />
        <SupportSection supportCards={supportCards} />
        <NextStepSection waLink={waLink} />
      </Box>
    </>
  );
}

function HeroSection({ waLink }) {
  return (
    <Box
      component='section'
      aria-labelledby='hero-junius-atto1-title'
      sx={{
        position: 'relative',
        minHeight: { xs: '86vh', md: '92vh' },
        display: 'flex',
        alignItems: 'flex-end',
        pt: { xs: 10, md: 11 },
        backgroundColor: '#111827',
      }}
    >
      <Box
        component='img'
        src={heroAtto1}
        alt='Konsultasi BYD Atto 1 dengan Junius'
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.2) 42%, rgba(0,0,0,0.82) 100%)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography
          sx={{
            display: 'inline-flex',
            px: 1.2,
            py: 0.65,
            borderRadius: '999px',
            backgroundColor: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            fontSize: '0.78rem',
            fontWeight: 900,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          Support Page
        </Typography>
        <Typography
          id='hero-junius-atto1-title'
          component='h1'
          sx={{
            mt: 1.6,
            fontSize: { xs: '2.2rem', sm: '3rem', md: '4.5rem' },
            fontWeight: 900,
            lineHeight: 0.98,
            maxWidth: 820,
          }}
        >
          Konsultasi BYD Atto 1 bersama Junius di BYD BSD Serpong
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 760,
            fontSize: { xs: '1rem', md: '1.08rem' },
            lineHeight: 1.8,
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          URL ini diposisikan sebagai halaman bantuan kontak dan follow up
          pembelian. Intent utama tetap diarahkan ke halaman produk dan halaman
          harga agar ranking keyword BYD Atto 1 tidak terpecah.
        </Typography>

        <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            href={waLink}
            target='_blank'
            rel='noopener noreferrer'
            variant='contained'
            sx={{
              px: 3.2,
              py: 1.1,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#1f5fd1',
              '&:hover': { backgroundColor: '#184ca8' },
            }}
          >
            Chat WhatsApp Junius
          </Button>
          <Button
            component={RouterLink}
            to='/blog/harga-byd-atto-1-bsd-serpong-junius'
            variant='outlined'
            sx={{
              px: 3.2,
              py: 1.1,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.72)',
              '&:hover': {
                borderColor: '#fff',
                backgroundColor: 'rgba(255,255,255,0.12)',
              },
            }}
          >
            Buka halaman harga
          </Button>
          <Button
            component={RouterLink}
            to='/jelajahi-atto1'
            variant='outlined'
            sx={{
              px: 3.2,
              py: 1.1,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.72)',
              '&:hover': {
                borderColor: '#fff',
                backgroundColor: 'rgba(255,255,255,0.12)',
              },
            }}
          >
            Buka halaman utama
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function SupportSection({ supportCards }) {
  return (
    <Box
      component='section'
      aria-labelledby='support-junius-title'
      sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, backgroundColor: '#fff' }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography
          sx={{
            color: '#4c5867',
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
          }}
        >
          Peran Halaman Ini
        </Typography>
        <Typography
          id='support-junius-title'
          component='h2'
          sx={{
            mt: 1,
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.05,
            maxWidth: 860,
          }}
        >
          Support page untuk kontak langsung, bukan money page utama
        </Typography>
        <Typography sx={{ mt: 1.5, maxWidth: 860, color: '#4b5563', lineHeight: 1.75 }}>
          Ini sengaja diatur agar pengguna yang memang mencari Junius tetap
          tertolong, tetapi keyword head term dan keyword lokal utama tetap
          diarahkan ke URL yang lebih tepat.
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {supportCards.map((item) => (
            <Box
              key={item.title}
              sx={{
                overflow: 'hidden',
                borderRadius: '8px',
                border: '1px solid rgba(17,24,39,0.08)',
                boxShadow: '0 12px 26px rgba(16,24,40,0.08)',
              }}
            >
              <Box
                component='img'
                src={item.image}
                alt={item.title}
                sx={{
                  width: '100%',
                  height: 240,
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <Box sx={{ p: 2.3 }}>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: '#111827' }}>
                  {item.title}
                </Typography>
                <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.7 }}>
                  {item.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function NextStepSection({ waLink }) {
  return (
    <Box
      component='section'
      aria-labelledby='next-step-junius-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#f4f7fb',
      }}
    >
      <Box
        sx={{
          maxWidth: 980,
          mx: 'auto',
          p: { xs: 2.5, md: 4 },
          borderRadius: '8px',
          backgroundColor: '#fff',
          border: '1px solid rgba(17,24,39,0.08)',
          boxShadow: '0 12px 28px rgba(16,24,40,0.08)',
          textAlign: 'center',
        }}
      >
        <Typography
          id='next-step-junius-title'
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
          }}
        >
          Langkah terbaik setelah halaman ini
        </Typography>
        <Typography
          sx={{
            mt: 2,
            mx: 'auto',
            maxWidth: 760,
            color: '#4b5563',
            lineHeight: 1.75,
          }}
        >
          Kalau Anda butuh overview produk, buka halaman utama BYD Atto 1. Kalau
          Anda sudah fokus ke harga dan promo, buka halaman harga. Kalau ingin
          langsung lanjut konsultasi, gunakan WhatsApp Junius.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Button
            component={RouterLink}
            to='/jelajahi-atto1'
            variant='outlined'
            sx={{
              px: 3.2,
              py: 1.15,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
              color: '#1f5fd1',
              borderColor: 'rgba(31,95,209,0.36)',
              '&:hover': {
                borderColor: '#1f5fd1',
                backgroundColor: '#edf4ff',
              },
            }}
          >
            Halaman utama BYD Atto 1
          </Button>
          <Button
            component={RouterLink}
            to='/blog/harga-byd-atto-1-bsd-serpong-junius'
            variant='outlined'
            sx={{
              px: 3.2,
              py: 1.15,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
              color: '#1f5fd1',
              borderColor: 'rgba(31,95,209,0.36)',
              '&:hover': {
                borderColor: '#1f5fd1',
                backgroundColor: '#edf4ff',
              },
            }}
          >
            Halaman harga lokal
          </Button>
          <Button
            href={waLink}
            target='_blank'
            rel='noopener noreferrer'
            variant='contained'
            sx={{
              px: 3.2,
              py: 1.15,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#1f5fd1',
              '&:hover': { backgroundColor: '#184ca8' },
            }}
          >
            Hubungi Junius
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default Atto1Junius;
